import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Layout } from './components/layout/Layout';
import { initializeCsrfToken } from './services/api';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { logger } from './utils/logger';

// Google OAuth Client ID
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
if (!GOOGLE_CLIENT_ID) {
  logger.error('VITE_GOOGLE_CLIENT_ID environment variable is required');
}

function ConfigErrorScreen() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 px-4">
      <div className="max-w-lg w-full bg-white border border-red-200 rounded-xl p-6">
        <h1 className="text-lg font-semibold text-red-700">OAuth configuration error</h1>
        <p className="mt-2 text-sm text-gray-700">
          Missing <code>VITE_GOOGLE_CLIENT_ID</code>. Admin login cannot start without a Google OAuth
          client ID.
        </p>
      </div>
    </div>
  );
}

// Lazy load pages for code splitting
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const Containers = lazy(() => import('./pages/Containers').then(m => ({ default: m.Containers })));
const ContainerDetail = lazy(() => import('./pages/ContainerDetail').then(m => ({ default: m.ContainerDetail })));
const Customers = lazy(() => import('./pages/Customers').then(m => ({ default: m.Customers })));
const Orders = lazy(() => import('./pages/Orders').then(m => ({ default: m.Orders })));
const Invoices = lazy(() => import('./pages/Invoices').then(m => ({ default: m.Invoices })));
const InvoiceDetail = lazy(() => import('./pages/InvoiceDetail').then(m => ({ default: m.InvoiceDetail })));
const Fleet = lazy(() => import('./pages/Fleet').then(m => ({ default: m.Fleet })));
const Bilaflotin = lazy(() => import('./pages/Bilaflotin').then(m => ({ default: m.Bilaflotin })));
const Alerts = lazy(() => import('./pages/Alerts').then(m => ({ default: m.Alerts })));
const Settings = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })));
const CMSList = lazy(() => import('./pages/cms').then(m => ({ default: m.CMSList })));
const EditSection = lazy(() => import('./pages/cms/EditSection').then(m => ({ default: m.EditSection })));
const Documentation = lazy(() => import('./pages/Documentation').then(m => ({ default: m.Documentation })));
const Markdown = lazy(() => import('./pages/Markdown').then(m => ({ default: m.Markdown })));
const Login = lazy(() => import('./pages/Login'));

// Admin pages
const UserManagement = lazy(() => import('./pages/UserManagement').then(m => ({ default: m.UserManagement })));
const FleetAssignment = lazy(() => import('./pages/FleetAssignment').then(m => ({ default: m.FleetAssignment })));
const ContainerAssignment = lazy(() => import('./pages/ContainerAssignment').then(m => ({ default: m.ContainerAssignment })));
const Feedback = lazy(() => import('./pages/Feedback').then(m => ({ default: m.Feedback })));
const HeroVideos = lazy(() => import('./pages/HeroVideos').then(m => ({ default: m.HeroVideos })));
const Analytics = lazy(() => import('./pages/Analytics').then(m => ({ default: m.Analytics })));
const RouteOptimization = lazy(() => import('./pages/RouteOptimization').then(m => ({ default: m.RouteOptimization })));
const StrategicAnalysis = lazy(() => import('./pages/StrategicAnalysis').then(m => ({ default: m.StrategicAnalysis })));
const ROICalculator = lazy(() => import('./pages/ROICalculator').then(m => ({ default: m.ROICalculator })));
const Calls = lazy(() => import('./pages/Calls'));
const Overview = lazy(() => import('./pages/Overview').then(m => ({ default: m.Overview })));
const Tickets = lazy(() => import('./pages/Tickets').then(m => ({ default: m.Tickets })));
const JobAssignment = lazy(() => import('./pages/JobAssignment').then(m => ({ default: m.JobAssignment })));

// PrivateRoute component - protects routes that require authentication
function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingFallback />;
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

// Loading fallback component
function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Hleð...</p>
      </div>
    </div>
  );
}

function App() {
  if (!GOOGLE_CLIENT_ID) {
    return <ConfigErrorScreen />;
  }

  // Initialize CSRF token on app load
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      // Only initialize CSRF token if user is authenticated
      initializeCsrfToken().catch((error) => {
        logger.warn('[App] Failed to initialize CSRF token:', error);
      });
    }
  }, []);

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
          <Route path="/login" element={<Login />} />

          <Route path="/" element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }>
            <Route index element={<Dashboard />} />
            <Route path="gamar" element={<Containers />} />
            <Route path="gamar/:id" element={<ContainerDetail />} />
            <Route path="vidskiptavinir" element={<Customers />} />
            <Route path="vidskiptavinir/:id" element={<Customers />} />
            <Route path="pantanir" element={<Orders />} />
            <Route path="pantanir/:id" element={<Orders />} />
            <Route path="reikningar" element={<Invoices />} />
            <Route path="reikningar/:id" element={<InvoiceDetail />} />
            <Route path="bilafloti" element={<Bilaflotin />} />
            <Route path="kortayfirlit" element={<Fleet />} />
            <Route path="tilkynningar" element={<Alerts />} />
            <Route path="cms" element={<CMSList />} />
            <Route path="cms/edit/:key" element={<EditSection />} />
            <Route path="skjolun" element={<Documentation />} />
            <Route path="markdown" element={<Markdown />} />
            <Route path="stillingar" element={<Settings />} />

            {/* Admin routes */}
            <Route path="notendur" element={<UserManagement />} />
            <Route path="bilafloti-skipulag" element={<FleetAssignment />} />
            <Route path="gamar-skipulag" element={<ContainerAssignment />} />
            <Route path="leida-bestun/:routeId" element={<RouteOptimization />} />
            <Route path="abendingar" element={<Feedback />} />
            <Route path="thjonustuver" element={<Tickets />} />
            <Route path="heimasida-myndbond" element={<HeroVideos />} />
            <Route path="greining" element={<Analytics />} />
            <Route path="stefnugreining" element={<StrategicAnalysis />} />
            <Route path="reikni" element={<ROICalculator />} />
            <Route path="simtol" element={<Calls />} />
            <Route path="verkefni-uthlutun" element={<JobAssignment />} />
            <Route path="yfirlit" element={<Overview />} />
          </Route>
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}

export default App;
